import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
    "group/button inline-flex shrink-0 items-center justify-center gap-(--space-1-5) font-medium whitespace-nowrap transition-all duration-150 outline-none select-none border border-transparent cursor-pointer disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default:
                    'bg-primary-500 text-primary-foreground hover:bg-primary-600 active:bg-primary-700 focus-visible:ring-3 focus-visible:ring-primary-200',
                outline:
                    'border-border-default bg-transparent text-text-primary hover:bg-bg-hover focus-visible:border-primary-300 focus-visible:ring-3 focus-visible:ring-primary-100',
                secondary:
                    'bg-neutral-100 text-text-secondary border-neutral-200 hover:bg-neutral-150 focus-visible:ring-3 focus-visible:ring-primary-200 dark:bg-bg-raised dark:text-text-primary dark:border-border-default dark:hover:border-border-strong',
                ghost: 'bg-transparent text-text-secondary hover:bg-bg-hover focus-visible:ring-3 focus-visible:ring-primary-200',
                destructive:
                    'bg-error-500 text-primary-foreground hover:bg-error-600 active:bg-error-700 focus-visible:ring-3 focus-visible:ring-error-200',
                warning:
                    'bg-warning-300 text-warning-900 hover:bg-warning-400 active:bg-warning-700 focus-visible:ring-3 focus-visible:ring-warning-200',
                link: 'text-primary-600 underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-9 px-3.5 text-sm rounded-md',
                sm: 'h-8 px-2.5 text-[0.8125rem] rounded-md',
                lg: 'h-10 px-[18px] text-base rounded-md',
                xs: "h-6 gap-1 px-2 text-xs rounded-sm [&_svg:not([class*='size-'])]:size-3",
                icon: 'size-9 rounded-md',
                'icon-xs': "size-6 rounded-sm [&_svg:not([class*='size-'])]:size-3",
                'icon-sm': 'size-8 rounded-md',
                'icon-lg': 'size-10 rounded-md',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

function Button({
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
    }) {
    const Comp = asChild ? Slot.Root : 'button';

    return (
        <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
}

export { Button, buttonVariants };
